/**
 * useGlobalTheme Hook — GLOBAL Brand Configuration (SuperAdmin-Controlled)
 * 
 * Fetches and applies the GLOBAL Look & Feel configuration for Admin/SuperAdmin users.
 * 
 * CRITICAL ARCHITECTURE NOTE:
 * ═══════════════════════════════════════════════════════════════════════════════
 * This hook manages GLOBAL brand colors (--global-primary, --global-accent) that
 * apply to ALL admin users REGARDLESS of their personal theme preferences.
 * 
 * HIERARCHY:
 * 1. Global theme (superadmin-controlled) → CSS variables: --global-primary, --global-accent
 * 2. Personal theme (user-level prefs)   → CSS variables: --primary, --accent, dark mode, fonts
 * 
 * IMPORTANT:
 * - Global colors are set at app init before personal theme is applied
 * - Global colors are NEVER overridden by personal preferences
 * - Personal theme customBrandColor (user HSL override) is stored separately and does
 *   NOT interact with global brand colors
 * 
 * CONTROLLED BY: SuperAdmin only via /super-admin/look-and-feel
 * APPLIES TO: All Admin + SuperAdmin users
 * STORED IN: PostgreSQL look_and_feel_config table (singleton, id=1)
 * 
 * Usage:
 * - Call useGlobalTheme() in the main Router to load global config on app init
 * - Call applyGlobalThemeColors(config) to apply colors via CSS variables
 * - Sidebar and core UI components use --global-primary and --global-accent CSS vars
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/http';
import { useAuth } from '@/lib/auth-store';

export interface GlobalThemeConfig {
  id: number;
  primaryColor: string;
  accentColor: string;
  navBgColor: string;
  navTextColor: string;
  navActiveColor: string;
  primaryHoverColor: string;
  fontFamily: string;
  logoUrl: string | null;
  updatedAt: string;
}

const DEFAULT_THEME: GlobalThemeConfig = {
  id: 1,
  primaryColor: '#3B82F6',      // Tailwind blue-500
  accentColor: '#1F2937',       // Tailwind gray-800
  navBgColor: '#0F3A3F',        // Dark blue nav
  navTextColor: '#FFFFFF',      // White text
  navActiveColor: '#0F3A3F',    // Reactor brand teal – active nav item
  primaryHoverColor: '#00917E', // Reactor brand green – primary button hover
  fontFamily: 'inter',
  logoUrl: null,
  updatedAt: new Date().toISOString(),
};

/**
 * Fetch global Look & Feel config from backend
 * 
 * Returns a TanStack Query result with global brand color configuration.
 * Safe to call multiple times; TanStack Query handles caching automatically.
 * 
 * Stale time: 5 minutes (other admins see changes within 5 min of SuperAdmin update, or on refresh)
 * On error: Falls back to DEFAULT_THEME with sensible defaults
 */
export function useGlobalTheme() {
  const { state } = useAuth();
  
  const query = useQuery<GlobalThemeConfig>({
    queryKey: ['global-theme'],
    queryFn: async () => {
      try {
        if (import.meta.env.DEV) {
          console.debug('[GLOBAL_THEME] Fetching global theme config from /api/look-and-feel...');
        }
        
        const response = await apiFetch('/api/look-and-feel');
        if (!response.ok) {
          console.warn('[GLOBAL_THEME] Failed to fetch global theme (HTTP ' + response.status + '), using defaults');
          return DEFAULT_THEME;
        }
        
        const data = await response.json();
        if (import.meta.env.DEV) {
          console.debug('[GLOBAL_THEME] Successfully fetched global theme:', data);
        }
        return data;
      } catch (error) {
        console.error('[GLOBAL_THEME] Error fetching global theme, using defaults:', error);
        return DEFAULT_THEME;
      }
    },
    staleTime: 5 * 60 * 1000,                      // 5 minutes - other admins see changes within 5 min
    gcTime: 10 * 60 * 1000,                        // 10 minutes - keep cached for performance
    retry: true,                                    // Retry failed requests
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: !!state.user,                          // Only fetch after user is authenticated (fixes race condition)
  });

  return query;
}

// Font family CSS mappings
const FONT_FAMILIES: Record<string, string> = {
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  roboto: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  openSans: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lato: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  montserrat: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mulish: "'Mulish', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  poppins: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  sourceSans: "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  nunito: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  raleway: "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  ubuntu: "'Ubuntu', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', monospace",
};

/**
 * Apply global theme colors to DOM via CSS variables
 * Call this after fetching the global config
 * 
 * This updates the root CSS variables that all UI components reference.
 * Safe to call multiple times; subsequent calls just update the variables.
 */
export function applyGlobalThemeColors(primaryColor: string, accentColor: string) {
  const root = document.documentElement;
  
  // Set CSS variables for global branding (used by sidebar, buttons, highlights, etc.)
  root.style.setProperty('--global-primary', primaryColor);
  root.style.setProperty('--global-accent', accentColor);
  
  // Development logging only
  if (import.meta.env.DEV) {
    console.debug('🎨 [GLOBAL_THEME] Applied global theme colors:', {
      primary: primaryColor,
      accent: accentColor,
    });
  }
}

/**
 * Apply full global theme config to DOM via CSS variables
 * Includes navigation colors and font family
 */
export function applyFullGlobalTheme(config: GlobalThemeConfig) {
  const root = document.documentElement;
  
  // Set CSS variables for global branding
  root.style.setProperty('--global-primary', config.primaryColor);
  root.style.setProperty('--global-accent', config.accentColor);
  root.style.setProperty('--navigation-bg', config.navBgColor);
  root.style.setProperty('--navigation-text', config.navTextColor);
  root.style.setProperty('--nav-active', config.navActiveColor || '#0F3A3F');
  root.style.setProperty('--primary-hover', config.primaryHoverColor || '#00917E');
  // Sync --primary so all default Button components use the global brand color
  root.style.setProperty('--primary', config.primaryColor);
  
  // Apply font family
  const fontStack = FONT_FAMILIES[config.fontFamily] || FONT_FAMILIES.inter;
  root.style.setProperty('--font-family-base', fontStack);
  document.body.style.fontFamily = fontStack;
  
  // Development logging only
  if (import.meta.env.DEV) {
    console.debug('🎨 [GLOBAL_THEME] Applied full global theme:', {
      primary: config.primaryColor,
      accent: config.accentColor,
      navBg: config.navBgColor,
      navText: config.navTextColor,
      navActive: config.navActiveColor,
      primaryHover: config.primaryHoverColor,
      fontFamily: config.fontFamily,
    });
  }
}
